// Stage 45: large sticky right-sidebar DOT widget. Mounts only on
// the chat view, only when dot_large_enabled === 'true'. Reads the
// same dot_* settings as the header DotWidget so the two widgets
// stay visually consistent when both are active (which they aren't
// in practice, since AuthedApp suppresses the header dot when this
// one is mounted).
//
// The supershape geometry, audio reactivity, and profile loading
// are all handled by the existing SupershapeDot component. This
// file owns: layout, mood-tinted background, and ambient mode.

import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { getOrCreateSpeechBus } from '../hooks/useAudioPlayer';
import { listDotProfiles, type DotProfileLite } from '../api/dotProfiles';
import { LargeDotWallpaper } from './LargeDotWallpaper';
import type { OrchestratorState } from '../hooks/useOrchestrator';

const SupershapeDot = lazy(() => import('./SupershapeDot'));

export type LargeDotMode = 'liquid' | 'flow' | 'static';

interface LargeDotWidgetProps {
  /** Master enable. When false, render nothing. */
  enabled: boolean;
  /** Stage 43 active-profile id (Settings → DOT). */
  activeProfileId: string;
  /** Stage 43 mesh colour. */
  color: string;
  /** Stage 43 wireframe toggle. */
  wireframe: boolean;
  /** Stage 43 sensitivity (`audio.reactiveAmount`). */
  sensitivity: number;
  /** Latest assistant mood label, or null when none has been seen. */
  moodLabel: string | null;
  /** Master toggle for the mood-tinted background. */
  moodBackgroundEnabled: boolean;
  /** Background animation mode used while idle. */
  idleMode: LargeDotMode;
  /** Background animation mode used while the assistant is working. */
  workingMode: LargeDotMode;
  /** Top-level orchestrator state. Drives idle vs working selection. */
  orchestratorState: OrchestratorState;
}

export function LargeDotWidget(
  props: LargeDotWidgetProps,
): JSX.Element | null {
  const {
    enabled,
    activeProfileId,
    color,
    wireframe,
    sensitivity,
    moodLabel,
    moodBackgroundEnabled,
    idleMode,
    workingMode,
    orchestratorState,
  } = props;

  const [ctx, setCtx] = useState<AudioContext | null>(null);
  const [analyser, setAnalyser] = useState<AnalyserNode | null>(null);
  const [profile, setProfile] = useState<unknown>(null);

  // Mirror DotWidget: acquire the shared speech bus so the analyser
  // sees bus.el (Gemini TTS, Kokoro, music gallery) AND ZeroTTS atoms.
  useEffect(() => {
    if (!enabled) return;
    const bus = getOrCreateSpeechBus();
    setCtx(bus.ctx);
    setAnalyser(bus.analyser);
  }, [enabled]);

  // Mirror DotWidget: resolve the profile and merge the live
  // sensitivity slider into audio.reactiveAmount.
  useEffect(() => {
    if (!enabled) {
      setProfile(null);
      return;
    }
    let cancelled = false;
    void listDotProfiles().then((list: DotProfileLite[]) => {
      if (cancelled) return;
      const hit =
        list.find((p) => p.id === activeProfileId) ?? list[0] ?? null;
      const merged = mergeSensitivity(hit?.profile ?? null, sensitivity);
      setProfile(merged);
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, activeProfileId, sensitivity]);

  // Resolve mode from orchestrator state. Anything that is not
  // 'thinking' counts as idle for this widget — speaking, listening,
  // etc. all visually belong to the calmer mode.
  const mode: LargeDotMode = useMemo(() => {
    if (orchestratorState === 'thinking') return workingMode;
    return idleMode;
  }, [orchestratorState, idleMode, workingMode]);

  if (!enabled || !analyser) return null;

  return (
    <aside className="large-dot" data-mode={mode} aria-hidden="true">
      <LargeDotWallpaper
        mode={mode}
        moodLabel={moodLabel}
        moodBackgroundEnabled={moodBackgroundEnabled}
        color={color}
        analyser={analyser}
      />
      <div className="large-dot-stage">
        <Suspense fallback={<div className="large-dot-fallback" />}>
          <SupershapeDot
            profile={profile as never}
            analyser={analyser}
            audioContext={ctx}
            color={color}
            wireframe={wireframe}
            autoRotate
          />
        </Suspense>
      </div>
    </aside>
  );
}

function mergeSensitivity(profile: unknown, sensitivity: number): unknown {
  if (!profile || typeof profile !== 'object') return profile;
  const p = profile as { audio?: Record<string, unknown> };
  return {
    ...(profile as object),
    audio: {
      ...(p.audio ?? {}),
      reactiveAmount: Math.max(0, Math.min(1, sensitivity)),
    },
  };
}
